<!-- SubscriptionCardModal.vue -->
<script setup>
import { ref, computed, watch } from "vue";
import { useSubscriptionStore } from "@/stores/useSubscriptionStore";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import api from "@/services/api";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, required: true },
  subscriptionId: { type: Number, required: true },
});

const emit = defineEmits(["close"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

const store = useSubscriptionStore();
const isLoadingData = ref(true);
const sub = ref(null);

// ─────────────────────────────────────────────
// FETCH — busca direto pelo ID para ter todos os campos
// ─────────────────────────────────────────────
watch(
  () => props.subscriptionId,
  async (newId) => {
    if (!newId) return;
    isLoadingData.value = true;
    sub.value = null;
    try {
      // Tenta primeiro no array já carregado da store (evita request extra)
      const cached = store.subscriptions.find(
        (s) => s.subscriptionId === newId
      );
      if (cached) {
        sub.value = cached;
      } else {
        const response = await api.get(`/subscriptions/${newId}`);
        sub.value = response.data;
      }
    } catch (e) {
      console.error("❌ Erro ao carregar assinatura no CardModal:", e);
    } finally {
      isLoadingData.value = false;
    }
  },
  { immediate: true }
);

// ─────────────────────────────────────────────
// NORMALIZAÇÃO DE STATUS (espelha SubscriptionForm.vue)
// ─────────────────────────────────────────────
function normalizeSubscriptionStatus(status) {
  if (status == null) return "pendingpayment";
  const s = String(status).trim().toLowerCase().replace(/[_\s]/g, "");
  const map = {
    active: "active",
    cancelled: "cancelled",
    expired: "expired",
    upgraded: "upgraded",
    pendingpayment: "pendingpayment",
    0: "active",
    1: "cancelled",
    2: "expired",
    3: "upgraded",
    4: "pendingpayment",
  };
  return map[s] ?? "pendingpayment";
}

function getSubscriptionStatusLabel(status) {
  const labels = {
    active: "Ativa",
    cancelled: "Cancelada",
    expired: "Expirada",
    upgraded: "Atualizada",
    pendingpayment: "Aguardando",
  };
  return labels[normalizeSubscriptionStatus(status)] ?? "Aguardando";
}

function getSubscriptionStatusClass(status) {
  const classes = {
    active: "badge-green",
    cancelled: "badge-red",
    expired: "badge-orange",
    upgraded: "badge-blue",
    pendingpayment: "badge-yellow",
  };
  return classes[normalizeSubscriptionStatus(status)] ?? "badge-yellow";
}

function normalizePaymentStatus(status) {
  if (status == null) return "none";
  const s = String(status).trim().toLowerCase();
  const map = {
    none: "none",
    pending: "pending",
    approved: "approved",
    rejected: "rejected",
    cancelled: "cancelled",
    refunded: "refunded",
    refundfailed: "refundfailed",
    0: "none",
    1: "pending",
    2: "approved",
    3: "rejected",
    4: "cancelled",
    5: "refunded",
    6: "refundfailed",
  };
  return map[s] ?? "none";
}

function getPaymentStatusLabel(status) {
  const labels = {
    none: "—",
    pending: "Pendente",
    approved: "Aprovado",
    rejected: "Rejeitado",
    cancelled: "Cancelado",
    refunded: "Estornado",
    refundfailed: "Revisar estorno",
  };
  return labels[normalizePaymentStatus(status)] ?? "—";
}

function getPaymentStatusClass(status) {
  const classes = {
    none: "badge-gray",
    pending: "badge-yellow",
    approved: "badge-green",
    rejected: "badge-red",
    cancelled: "badge-red",
    refunded: "badge-purple",
    refundfailed: "badge-orange",
  };
  return classes[normalizePaymentStatus(status)] ?? "badge-gray";
}

function normalizePreapprovalStatus(status) {
  if (status == null) return "none";
  const s = String(status).trim().toLowerCase();
  const map = {
    none: "none",
    pending: "pending",
    authorized: "authorized",
    cancelled: "cancelled",
    0: "none",
    1: "pending",
    2: "authorized",
    3: "cancelled",
  };
  return map[s] ?? "none";
}

function getPreapprovalStatusLabel(status) {
  const labels = {
    none: "—",
    pending: "Pendente",
    authorized: "Autorizado",
    cancelled: "Cancelado",
  };
  return labels[normalizePreapprovalStatus(status)] ?? "—";
}

function getPreapprovalStatusClass(status) {
  const classes = {
    none: "badge-gray",
    pending: "badge-yellow",
    authorized: "badge-green",
    cancelled: "badge-red",
  };
  return classes[normalizePreapprovalStatus(status)] ?? "badge-gray";
}

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
const isRecurring = computed(() =>
  sub.value ? !!sub.value.mpSubscriptionId : false
);

// ✅ NOVO: mapa completo de chips por método de pagamento
const paymentMethodChip = computed(() => {
  const raw = (sub.value?.paymentMethod ?? "").toLowerCase().trim();

  const map = {
    courtesy: { label: "Cortesia", icon: "fas fa-gift", cls: "badge-courtesy" },
    pix: { label: "PIX", icon: "fas fa-qrcode", cls: "badge-green" },
    credit_card: {
      label: "Cartão",
      icon: "fas fa-credit-card",
      cls: "badge-blue",
    },
    creditcard: {
      label: "Cartão",
      icon: "fas fa-credit-card",
      cls: "badge-blue",
    },
    debit_card: {
      label: "Débito",
      icon: "fas fa-credit-card",
      cls: "badge-blue",
    },
    debitcard: {
      label: "Débito",
      icon: "fas fa-credit-card",
      cls: "badge-blue",
    },
    ticket: { label: "Boleto", icon: "fas fa-barcode", cls: "badge-orange" },
    boleto: { label: "Boleto", icon: "fas fa-barcode", cls: "badge-orange" },
    account_money: {
      label: "Saldo MP",
      icon: "fas fa-wallet",
      cls: "badge-yellow",
    },
    free: { label: "Free", icon: "fas fa-star", cls: "badge-gray" },
    trial: { label: "Trial", icon: "fas fa-clock", cls: "badge-yellow" },
    bank_transfer: {
      label: "Transferência",
      icon: "fas fa-university",
      cls: "badge-blue",
    },
  };

  if (!raw)
    return { label: "—", icon: "fas fa-question-circle", cls: "badge-gray" };

  // Recorrente sobrepõe o método visual (complemento ao chip de método)
  const base = map[raw] ?? {
    label: sub.value.paymentMethod,
    icon: "fas fa-money-bill-wave",
    cls: "badge-gray",
  };
  return base;
});

function displayVal(v) {
  return v != null && v !== "" ? v : "—";
}
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="emit('close')">
    <div ref="panelRef" class="card-modal" :style="panelStyle">
      <!-- HEADER -->
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="card-title">
          Detalhes da Assinatura
          <span v-if="sub" class="title-id"
            >(ID: {{ sub.subscriptionId }})</span
          >
        </h3>
        <button
          @click="emit('close')"
          class="modal-close-btn"
          aria-label="Fechar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <!-- LOADING -->
      <div v-if="isLoadingData" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i> Carregando detalhes...
      </div>

      <!-- CONTEÚDO -->
      <div v-else-if="sub" class="card-body">
        <!-- ── SEÇÃO: Identificação ───────────────────────────────── -->
        <section class="card-section">
          <h4 class="section-title">Identificação</h4>

          <!-- Linha 1: IDs + nomes -->
          <div class="info-grid info-grid-3col" style="margin-bottom: 0.5rem">
            <div class="info-item">
              <span class="info-label">ID Assinatura</span>
              <span class="info-value mono">{{ sub.subscriptionId }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Usuário</span>
              <span class="info-value mono">
                {{ sub.userName ?? sub.userId }}
                <span v-if="sub.userName" class="id-muted"
                  >({{ sub.userId }})</span
                >
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">Plano</span>
              <span class="info-value mono">
                {{ sub.planName ?? sub.planId }}
                <span v-if="sub.planName" class="id-muted"
                  >({{ sub.planId }})</span
                >
              </span>
            </div>
          </div>

          <!-- Linha 2: Método + Status + Pagamento + Preapproval na mesma linha -->
          <div class="info-grid info-grid-4col">
            <div class="info-item info-item-chip">
              <span class="info-label">Método Pagamento</span>
              <span class="method-chips">
                <span :class="['badge', paymentMethodChip.cls]">
                  <i :class="paymentMethodChip.icon"></i>
                  {{ paymentMethodChip.label }}
                </span>
                <span v-if="isRecurring" class="badge badge-purple">
                  <i class="fas fa-sync-alt"></i> Recorrente
                </span>
              </span>
            </div>
            <div class="info-item info-item-chip">
              <span class="info-label">Status</span>
              <span :class="['badge', getSubscriptionStatusClass(sub.status)]">
                {{ getSubscriptionStatusLabel(sub.status) }}
              </span>
            </div>
            <div class="info-item info-item-chip">
              <span class="info-label">Pagamento</span>
              <span
                :class="['badge', getPaymentStatusClass(sub.paymentStatus)]"
              >
                {{ getPaymentStatusLabel(sub.paymentStatus) }}
              </span>
            </div>
            <div class="info-item info-item-chip">
              <span class="info-label">Preapproval</span>
              <span
                :class="[
 'badge',
                  getPreapprovalStatusClass(sub.preapprovalStatus),
                ]"
              >
                {{ getPreapprovalStatusLabel(sub.preapprovalStatus) }}
              </span>
            </div>
          </div>
        </section>

        <hr class="divider" />

        <!-- ── SEÇÃO: Detalhe de Status ───────────────────────────── -->
        <section class="card-section">
          <h4 class="section-title">Detalhe de Status</h4>
          <blockquote class="status-quote">
            {{ displayVal(sub.statusDetail) }}
          </blockquote>
        </section>

        <hr class="divider" />

        <!-- ── SEÇÃO: Vigência — todos os campos em uma única linha ── -->
        <section class="card-section">
          <h4 class="section-title">Vigência</h4>
          <div class="info-grid info-grid-5col">
            <div class="info-item">
              <span class="info-label">Data Início</span>
              <span class="info-value">{{
                formatDateTimeForDisplay(sub.startDate)
              }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Data Fim</span>
              <span class="info-value">
                {{ sub.endDate ? formatDateTimeForDisplay(sub.endDate) : "—" }}
              </span>
            </div>
            <!-- ✅ NOVO CAMPO ADICIONADO -->
            <div class="info-item">
              <span class="info-label">Cancelado em</span>
              <span class="info-value">
                {{ sub.cancelledAt ? formatDateTimeForDisplay(sub.cancelledAt) : "—" }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">Criado em</span>
              <span class="info-value">{{
                formatDateTimeForDisplay(sub.createdAt)
              }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Atualizado em</span>
              <span class="info-value">{{
                formatDateTimeForDisplay(sub.updatedAt)
              }}</span>
            </div>
          </div>
        </section>

        <hr class="divider" />

        <!-- ── SEÇÃO: Mercado Pago ─────────────────────────────────
             Linha 1: MP Payment ID | MP Subscription ID
             Linha 2: MP Card ID | MP Payment Method ID | MP Preference ID  -->
        <section class="card-section">
          <h4 class="section-title">Mercado Pago</h4>

          <!-- Grid 3×2:
               L1C1: MP Payment ID | L1C2: MP Subscription ID | L1C3: (vazio)
               L2C1: MP Card ID    | L2C2: MP Payment Method ID | L2C3: MP Preference ID -->
          <div class="info-grid info-grid-mp">
            <div class="info-item">
              <span class="info-label">MP Payment ID</span>
              <span class="info-value mono copyable">{{
                displayVal(sub.mpPaymentId)
              }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">MP Subscription ID</span>
              <span class="info-value mono copyable copyable-wrap">{{
                displayVal(sub.mpSubscriptionId)
              }}</span>
            </div>
            <!-- L1C3 vazia -->
            <div></div>
            <div class="info-item">
              <span class="info-label">MP Card ID</span>
              <span class="info-value mono copyable">{{
                displayVal(sub.mpCardId)
              }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">MP Payment Method ID</span>
              <span class="info-value mono copyable">{{
                displayVal(sub.mpPaymentMethodId)
              }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">MP Preference ID</span>
              <span class="info-value mono copyable">{{
                displayVal(sub.mpPreferenceId)
              }}</span>
            </div>
          </div>
        </section>
      </div>

      <!-- ERRO / vazio -->
      <div v-else class="loading-state">Assinatura não encontrada.</div>
    </div>
  </div>
</template>

<style scoped>
/* ── CONTAINER ─────────────────────────────────────────────── */
.card-modal {
  background: var(--surface);
  border-radius: 12px;
  width: 90%;
  max-width: 680px;
  box-shadow: var(--shadow-modal);
  margin-bottom: 50px;
  overflow: hidden;
  /* ✅ altura máxima um pouco maior que a viewport; header fixo, body rola */
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
}

.title-id {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 4px;
}

/* ── BODY ──────────────────────────────────────────────────── */
.card-body {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow-y: auto;
}

/* ── BADGES ────────────────────────────────────────────────── */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 9999px;
  white-space: nowrap;
}

.badge-green {
  background-color: #b2f2bb;
  color: #2b8a3e;
}
.badge-red {
  background-color: #ffc9c9;
  color: #c92a2a;
}
.badge-yellow {
  background-color: var(--warning-bg);
  color: #854d0e;
}
.badge-orange {
  background-color: #ffe8cc;
  color: #9c4a00;
}
.badge-blue {
  background-color: #d0ebff;
  color: #1864ab;
}
.badge-purple {
  background-color: #e9d5ff;
  color: #6b21a8;
}
.badge-gray {
  background-color: #f1f3f5;
  color: #495057;
}
.badge-courtesy {
  background-color: var(--brand-soft);
  color: var(--text-on-brand-soft);
  border: 1px solid #c4b5fd;
}

/* ── SEÇÕES ────────────────────────────────────────────────── */
.card-section {
  padding: 0.25rem 0 0.75rem;
}

.section-title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-subtle);
  margin: 0 0 0.6rem 0;
}

.divider {
  border: none;
  border-top: 1px dashed var(--border-color);
  margin: 0 0 0.75rem 0;
}

/* ── GRID DE CAMPOS ────────────────────────────────────────── */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.5rem 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-item-full {
  grid-column: 1 / -1;
}

/* ✅ badge encolhe para o tamanho do seu conteúdo */
.info-item-chip {
  align-items: flex-start;
}

.info-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
}

/* ✅ NOVO: chips empilhados (método + recorrente) no campo Método Pagamento */
.method-chips {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.3rem;
  margin-top: 2px;
}

.info-value {
  font-size: 0.88rem;
  color: var(--text-dark);
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.82rem;
}

/* ✅ ID entre parênteses em cinza claro (ex: "Free (3)") */
.id-muted {
  color: var(--text-subtle);
  font-size: 0.78rem;
  margin-left: 2px;
}

/* IDs do MP — truncar com ellipsis se muito longo */
.copyable {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  display: block;
  cursor: default;
}
.copyable:hover {
  overflow: visible;
  white-space: normal;
  word-break: break-all;
}
/* ✅ variante que sempre exibe o conteúdo completo, sem truncar */
.copyable-wrap {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
  word-break: break-all;
}

/* ── BLOCKQUOTE DE STATUS DETAIL ───────────────────────────── */
.status-quote {
  margin: 0;
  padding: 0.4rem 0 0.4rem 0.75rem;
  border-left: 4px solid var(--brand-soft);
  color: var(--text-light);
  font-size: 0.88rem;
  line-height: 1.6;
  white-space: pre-wrap;
  background: transparent;
}

/* ── GRID VARIANTS ─────────────────────────────────────────── */
.info-grid-4col {
  grid-template-columns: repeat(4, 1fr) !important;
}

/* Força 5 colunas iguais (Vigência — todos numa linha) */
.info-grid-5col {
  grid-template-columns: repeat(5, 1fr) !important;
}

/* ── GRID MERCADO PAGO — 3 colunas × 2 linhas ─────────────────
   L1: MP Payment ID | MP Subscription ID | (vazio)
   L2: MP Card ID    | MP Payment Method ID | MP Preference ID */
.info-grid-mp {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem 1rem;
}
</style>